import Phaser from "phaser";
import type { Direction } from "../models/ModuleGrid.js";
import { shapeDef } from "../data/moduleShapes.js";
import { t } from "../i18n/i18n.js";

/** 효과 모듈 → 셀 표시 약어 (현지화: effect.<id>). 아이콘 미등록 모듈의 fallback 표기. */
export function effectAbbr(moduleId: string): string {
  return t(`effect.${moduleId}`);
}

/** shapeId → 포트 방향(방향 명시 카탈로그라 회전 없이 고정). */
export function portsFor(shapeId: string): Direction[] {
  return [...shapeDef(shapeId).ports];
}

/**
 * 레일 1획 — 굵은 저알파 글로우 + 얇은 코어 2겹으로 "구슬이 지나는 발광 레일"을 그린다.
 * 단선(회로 인상)을 피하는 dark neon 표현 — 색 토큰은 호출자 주입(추가 토큰 없이 동일 색 2겹).
 */
function strokeRail(
  g: Phaser.GameObjects.Graphics,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  color: number,
  alpha: number,
): void {
  g.lineStyle(7, color, alpha * 0.28); // 글로우
  g.beginPath();
  g.moveTo(x0, y0);
  g.lineTo(x1, y1);
  g.strokePath();
  g.lineStyle(2.5, color, alpha); // 코어
  g.beginPath();
  g.moveTo(x0, y0);
  g.lineTo(x1, y1);
  g.strokePath();
}

/** 레일 합류점의 구슬 — 점(회로 노드)이 아니라 글로우 원 + 코어 원의 발광 구슬로. */
function drawOrb(g: Phaser.GameObjects.Graphics, x: number, y: number, color: number, alpha: number): void {
  g.fillStyle(color, alpha * 0.25);
  g.fillCircle(x, y, 6);
  g.fillStyle(color, alpha);
  g.fillCircle(x, y, 3.2);
}

/**
 * 모양 포트를 셀 중심(container-local 0,0) 기준으로 발광 레일 + 중심 구슬로 그린다 (지시문 §16).
 * "구슬이 통과하며 강화되는 발사 경로" 표현 — caller가 Graphics를 container.add한 뒤 호출.
 */
export function drawShapeLines(
  g: Phaser.GameObjects.Graphics,
  ports: readonly Direction[],
  half: number,
  color: number,
  alpha = 1,
): void {
  g.clear();
  for (const p of ports) {
    if (p === "up") strokeRail(g, 0, 0, 0, -half, color, alpha);
    else if (p === "down") strokeRail(g, 0, 0, 0, half, color, alpha);
    else if (p === "left") strokeRail(g, 0, 0, -half, 0, color, alpha);
    else strokeRail(g, 0, 0, half, 0, color, alpha);
  }
  drawOrb(g, 0, 0, color, alpha);
}

/**
 * 셀별 포트 레일 — 각 셀 offset(ox,oy)에서 그 셀의 실제 포트 방향으로 발광 레일 + 구슬.
 * 다중 셀 모듈의 셀마다 다른 고유 모양(직선/꺾임/분기)을 표현 (cellPortsForPlaced 결과).
 * caller가 Graphics를 모듈(footprint) 중심에 위치시킨 뒤 셀별 {offset, ports} 리스트를 넘긴다.
 */
export function drawCellPorts(
  g: Phaser.GameObjects.Graphics,
  cells: readonly { ox: number; oy: number; ports: readonly Direction[] }[],
  half: number,
  color: number,
  alpha = 1,
): void {
  g.clear();
  for (const { ox, oy, ports } of cells) {
    for (const p of ports) {
      if (p === "up") strokeRail(g, ox, oy, ox, oy - half, color, alpha);
      else if (p === "down") strokeRail(g, ox, oy, ox, oy + half, color, alpha);
      else if (p === "left") strokeRail(g, ox, oy, ox - half, oy, color, alpha);
      else strokeRail(g, ox, oy, ox + half, oy, color, alpha);
    }
    drawOrb(g, ox, oy, color, alpha);
  }
}
